import moment from 'moment'
import React from 'react'
import { Button, Icon } from 'semantic-ui-react'
import { CSVLink } from '../../../../../../common-components/react-csv-fixed'
import createAction from '../../../../../../redux/actions/createAction'
import {
  RESET_EXPORTED_KEG_COLLECTION_BOOKINGS_CSV_FILE,
  FETCH_EXPORT_KEG_COLLECTION_BOOKINGS_TO_CSV,
} from '../../../../../../redux/actions/actionTypes'
import { formatBookingsCSVExportData } from '../../../utils/tableDataFormatter'

const csvFilename = () => {
  const now = moment()
  const datePart = now.format('YYYY-MM-DD')
  const timePart = now.format('HHmm')
  return `keg_collection_bookings_${datePart}_${timePart}.csv`
}

const BookingsExportButton = ({
  translate,
  localeCode,
  bookingsAPIParams,
  dispatch,
  bookingsExportList: { content, isFetching, didFetchFail, didFetchSucceed },
  collectorsList,
  isDisabled,
}) => {
  const csvLinkRef = React.useRef(null)

  const exportData = React.useMemo(() => {
    if (!content || content.length === 0) {
      return []
    }
    return formatBookingsCSVExportData(content, translate, localeCode, collectorsList.content)
  }, [content, translate, localeCode, collectorsList.content])

  // State to trigger download when export data is ready
  const [shouldTriggerDownload, setShouldTriggerDownload] = React.useState(false)

  // Set flag when export data is ready
  React.useEffect(() => {
    if (didFetchSucceed && exportData.length > 0) {
      setShouldTriggerDownload(true)
    }
  }, [didFetchSucceed, exportData])

  // Kick off CSV download when DOM is committed
  React.useLayoutEffect(() => {
    if (shouldTriggerDownload && csvLinkRef.current?.link) {
      csvLinkRef.current.link.click()
      dispatch(createAction(RESET_EXPORTED_KEG_COLLECTION_BOOKINGS_CSV_FILE))
      setShouldTriggerDownload(false)
    }
  }, [shouldTriggerDownload, dispatch])

  const handleClick = () => {
    dispatch(
      createAction(FETCH_EXPORT_KEG_COLLECTION_BOOKINGS_TO_CSV, {
        bookingsAPIParams,
      })
    )
  }

  const errorButtonContent = (
    <>
      <Icon name="exclamation triangle" style={{ color: 'red' }} />
      <span style={{ marginLeft: 6, color: 'red' }}>
        {translate('bookingsExportCSVFailureBtnLbl')}
      </span>
    </>
  )

  const buttonContent = (
    <>
      {isFetching ? <Icon loading name="circle notch" /> : <Icon name="file excel outline" />}
      <span style={{ marginLeft: 6 }}>
        {isFetching ? translate('bookingsExportCSVPreparingLbl') : translate('exportCSV')}
      </span>
    </>
  )

  return (
    <>
      <Button
        basic
        icon
        size="small"
        title={translate('exportCSV')}
        aria-label={translate('exportCSV')}
        disabled={isFetching || isDisabled}
        onClick={handleClick}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        {didFetchFail ? errorButtonContent : buttonContent}
      </Button>

      <CSVLink
        data={exportData}
        filename={csvFilename()}
        ref={csvLinkRef}
        style={{ display: 'none' }}
      />
    </>
  )
}

export default React.memo(BookingsExportButton)
